// src/components/ProductionDashboard/WorkerReport.js
// Task 13 — Worker Search Report: Weekly/Monthly Activity, Attendance & Payroll
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Star, Calendar, Award,
    Package, Layers, ArrowLeft, User, FileText,
    Gem, Trophy, DollarSign, AlertTriangle, ChevronRight, Printer, RefreshCcw,
    UserCheck
} from 'lucide-react';
import { productionApi } from '../api';
import SkeletonLoader from './components/SkeletonLoader';
import ApiButton from './components/ApiButton';
import { BREAKPOINTS } from './productionTheme';

// ─── STAR RENDERER ───
const StarRating = ({ stars, size = 18 }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
            <Star key={i} size={size}
                className={i <= stars ? 'text-amber-400' : 'text-slate-200'}
                fill={i <= stars ? 'currentColor' : 'none'}
                strokeWidth={i <= stars ? 0 : 1.5}
            />
        ))}
    </div>
);

// ─── METRIC CARD ───
const MetricCard = ({ icon: Icon, label, value, suffix = '', color = 'indigo', sub }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center`}
                style={{ backgroundColor: `var(--color-${color}, #eef2ff)` }}>
                <Icon size={16} className={`text-${color}-600`} />
            </div>
        </div>
        <div className="text-2xl font-bold text-slate-800">
            {value}<span className="text-sm text-slate-400 ml-1">{suffix}</span>
        </div>
        <div className="text-[10px] font-bold text-slate-400 uppercase mt-1">{label}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
);

// ─── MINI BAR CHART ───
const MiniBarChart = ({ data, dataKey, label, color = 'bg-indigo-500', maxBars = 30 }) => {
    const displayData = data.slice(-maxBars);
    if (displayData.length === 0) return (
        <div className="text-center py-6 text-slate-400 text-xs">No data in this period</div>
    );
    const max = Math.max(...displayData.map(d => d[dataKey] || 0), 1);
    return (
        <div>
            <div className="text-xs font-bold text-slate-500 uppercase mb-3">{label}</div>
            <div className="flex items-end gap-1 h-20">
                {displayData.map((d, i) => {
                    const val = d[dataKey] || 0;
                    const pct = Math.max((val / max) * 100, 4);
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center group relative">
                            <div className={`w-full ${color} rounded-t transition-all hover:opacity-80`}
                                style={{ height: `${pct}%`, minHeight: '2px' }}
                                title={`${d.date}: ${val}`} />
                            <div className="absolute -top-8 bg-slate-800 text-white text-[9px] px-1.5 py-0.5 rounded hidden group-hover:block whitespace-nowrap z-10">
                                {d.date?.slice(5)}: {val}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex justify-between text-[9px] text-slate-400 mt-1 font-mono">
                <span>{displayData[0]?.date?.slice(5)}</span>
                <span>{displayData[displayData.length - 1]?.date?.slice(5)}</span>
            </div>
        </div>
    );
};


// ═══════════════════════════════════════════
// SEARCH BAR WITH AUTOCOMPLETE
// ═══════════════════════════════════════════
const WorkerSearchBar = ({ onSelect }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [allWorkers, setAllWorkers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);

    // Load all workers on mount
    useEffect(() => {
        const loadAll = async () => {
            setLoading(true);
            try {
                const res = await productionApi.get('/workers/search?q=');
                const data = res.data?.data || [];
                setAllWorkers(data);
                setResults(data);
                console.log('[WorkerReport] Loaded workers:', data.length);
            } catch (e) {
                console.error('[WorkerReport] Failed to load workers:', e);
                setAllWorkers([]);
                setResults([]);
            }
            setLoading(false);
        };
        loadAll();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const doSearch = useCallback(async (q) => {
        if (!q || q.length < 1) {
            setResults(allWorkers);
            return;
        }
        setLoading(true);
        try {
            const res = await productionApi.get(`/workers/search?q=${encodeURIComponent(q)}`);
            setResults(res.data?.data || []);
            setShowDropdown(true);
        } catch (e) {
            console.error('[WorkerReport] Search error:', e);
            setResults([]);
        }
        setLoading(false);
    }, [allWorkers]);

    const handleChange = (val) => {
        setQuery(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(val), 300);
    };

    const handleSelect = (w) => {
        setQuery(w.name);
        setShowDropdown(false);
        onSelect(w);
    };

    return (
        <div ref={wrapperRef} className="relative w-full max-w-lg">
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                    type="text"
                    placeholder="Search worker by name, code, or role..."
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 text-sm bg-white shadow-sm transition-all"
                    value={query}
                    onChange={e => handleChange(e.target.value)}
                    onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
                />
                {loading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
            </div>

            {showDropdown && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-2xl shadow-slate-200/60 z-50 max-h-80 overflow-y-auto">
                    {results.map(w => (
                        <button
                            key={w.id}
                            onClick={() => handleSelect(w)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-indigo-50 transition-colors text-left border-b border-slate-50 last:border-0"
                        >
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {(w.name || 'W')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-800 text-sm">{w.name}</div>
                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                    <span className="font-mono bg-slate-100 px-1.5 rounded">{w.worker_code}</span>
                                    <span>{w.role}</span>
                                    {w.department && <span className="text-indigo-600">• {w.department}</span>}
                                </div>
                            </div>
                            <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
                        </button>
                    ))}
                </div>
            )}

            {showDropdown && query.length > 0 && results.length === 0 && !loading && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg z-50 p-6 text-center">
                    <User size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">No workers found for "{query}"</p>
                </div>
            )}

            {/* Show worker count below search */}
            {!showDropdown && allWorkers.length > 0 && !loading && (
                <div className="mt-2 text-xs text-slate-400">
                    {allWorkers.length} workers available — click the search box to browse
                </div>
            )}
        </div>
    );
};


// ═══════════════════════════════════════════
// WORKER REPORT DETAIL VIEW
// ═══════════════════════════════════════════
const ReportView = ({ worker, period, onChangePeriod }) => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadReport = useCallback(async () => {
        setLoading(true);
        try {
            const res = await productionApi.get(`/workers/${worker.id}/report?period=${period}`);
            if (res.data?.success) setReport(res.data);
        } catch (e) {
            console.error('Report error:', e);
        }
        setLoading(false);
    }, [worker.id, period]);

    useEffect(() => { loadReport(); }, [loadReport]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) return (
        <div className="py-6">
            <SkeletonLoader count={4} height={72} />
        </div>
    );

    if (!report) return (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
            <AlertTriangle size={32} className="mb-2" />
            <p>Failed to load report. Try again.</p>
        </div>
    );

    const { performance, attendance, stages_summary, salary, products_made, daily_activity, stage_detail } = report;

    return (
        <div className="space-y-6 print:space-y-4">

            {/* Worker Header */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-between print:shadow-none">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg print:shadow-none">
                        {(report.worker?.name || 'W')[0].toUpperCase()}
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-3">
                            {report.worker?.name}
                            {performance?.bonus_eligible && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold border border-emerald-200 flex items-center gap-1">
                                    <Award size={11} /> Bonus Eligible
                                </span>
                            )}
                        </h2>
                        <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                            <span className="font-mono bg-slate-100 px-1.5 rounded">{report.worker?.worker_code}</span>
                            <span>{report.worker?.role}</span>
                            {report.worker?.department && <span className="text-indigo-600">• {report.worker?.department}</span>}
                            <span className="text-slate-400">₹{report.worker?.daily_wage}/day</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            <StarRating stars={performance?.stars || 0} size={16} />
                            <span className="text-sm font-bold text-amber-600">{performance?.stars || 0}/5</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 print:hidden">
                    <select
                        className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                        value={period}
                        onChange={e => onChangePeriod(e.target.value)}
                    >
                        <option value="week">Last Week</option>
                        <option value="month">Last Month</option>
                    </select>
                    <ApiButton onClick={loadReport} className="p-2 min-w-[44px]" title="Refresh">
                        <RefreshCcw size={16} />
                    </ApiButton>
                    <ApiButton onClick={handlePrint} className="text-sm print:hidden">
                        <Printer size={14} /> Print / PDF
                    </ApiButton>
                </div>
            </div>

            {/* Period Banner */}
            <div className="bg-indigo-50 rounded-lg px-5 py-2 flex items-center gap-2 text-sm text-indigo-700 font-medium border border-indigo-100">
                <Calendar size={14} />
                Report Period: <span className="font-bold">{report.period?.start}</span> →{' '}
                <span className="font-bold">{report.period?.end}</span>
                <span className="text-xs bg-indigo-200 px-2 py-0.5 rounded-full ml-2 font-bold uppercase">{report.period?.type}</span>
            </div>

            {/* KPI Summary Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <MetricCard icon={Star} label="Performance Stars" value={performance?.stars || 0} suffix="/5" color="amber" />
                <MetricCard icon={UserCheck} label="Attendance" value={`${attendance?.attendance_pct || 0}`} suffix="%" color="emerald"
                    sub={`${attendance?.present_days || 0} of ${attendance?.working_days || 0} days`} />
                <MetricCard icon={Layers} label="Stages Done" value={stages_summary?.total_stages || 0} color="indigo"
                    sub={`${stages_summary?.on_time || 0} on-time`} />
                <MetricCard icon={Package} label="Pieces Made" value={stages_summary?.total_pieces || 0} color="purple" />
                <MetricCard icon={DollarSign} label="Net Pay" value={`₹${(salary?.net_pay || 0).toLocaleString()}`} color="blue"
                    sub={salary?.status !== 'N/A' ? `Status: ${salary?.status}` : ''} />
                <MetricCard icon={Award} label="Bonus Status" value={performance?.bonus_eligible ? 'Eligible' : 'Not Yet'} color="emerald" />
            </div>

            {/* Two-Column: Attendance + Salary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Attendance Card */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="bg-emerald-50 px-5 py-3 border-b border-emerald-100">
                        <h3 className="font-bold text-emerald-800 text-sm flex items-center gap-2">
                            <UserCheck size={16} /> Attendance Summary
                        </h3>
                    </div>
                    <div className="p-5 space-y-3">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="bg-emerald-50 rounded-lg p-3">
                                <div className="text-2xl font-black text-emerald-700">{attendance?.present_days || 0}</div>
                                <div className="text-[10px] text-emerald-600 font-bold uppercase">Present</div>
                            </div>
                            <div className="bg-red-50 rounded-lg p-3">
                                <div className="text-2xl font-black text-red-600">{attendance?.absent_days || 0}</div>
                                <div className="text-[10px] text-red-500 font-bold uppercase">Absent</div>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3">
                                <div className="text-2xl font-black text-amber-600">{attendance?.half_days || 0}</div>
                                <div className="text-[10px] text-amber-500 font-bold uppercase">Half Day</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Working Days:</span>
                            <span className="font-bold text-slate-800">{attendance?.working_days || 0}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Total Hours Worked:</span>
                            <span className="font-bold text-slate-800">{attendance?.total_hours_worked || 0}h</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all ${(attendance?.attendance_pct || 0) >= 90 ? 'bg-emerald-500' : (attendance?.attendance_pct || 0) >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(attendance?.attendance_pct || 0, 100)}%` }}
                            />
                        </div>
                        <div className="text-xs text-slate-400 text-right font-bold">{attendance?.attendance_pct || 0}% attendance rate</div>
                    </div>
                </div>

                {/* Salary Card */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="bg-blue-50 px-5 py-3 border-b border-blue-100">
                        <h3 className="font-bold text-blue-800 text-sm flex items-center gap-2">
                            <DollarSign size={16} /> Salary & Payroll
                        </h3>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-blue-50 rounded-lg p-4">
                                <div className="text-xl font-black text-blue-700">₹{(salary?.gross || 0).toLocaleString()}</div>
                                <div className="text-[10px] text-blue-500 font-bold uppercase">Gross Salary</div>
                            </div>
                            <div className="bg-emerald-50 rounded-lg p-4">
                                <div className="text-xl font-black text-emerald-700">₹{(salary?.net_pay || 0).toLocaleString()}</div>
                                <div className="text-[10px] text-emerald-500 font-bold uppercase">Net Pay</div>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Deductions:</span>
                            <span className="font-bold text-red-600">₹{(salary?.deductions || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">Payment Status:</span>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${salary?.status === 'paid' || salary?.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' :
                                salary?.status === 'pending' || salary?.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                    'bg-slate-100 text-slate-500'
                                }`}>
                                {salary?.status || 'N/A'}
                            </span>
                        </div>
                        {salary?.reference && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500">Reference:</span>
                                <span className="font-mono text-xs text-slate-600">{salary.reference}</span>
                            </div>
                        )}
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                            <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">Daily Wage Rate</div>
                            <div className="text-lg font-black text-slate-700">₹{(report.worker?.daily_wage || 0).toLocaleString()}/day</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:break-inside-avoid">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <MiniBarChart data={daily_activity || []} dataKey="pieces_produced"
                        label="Pieces Produced / Day" color="bg-emerald-500" />
                </div>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                    <MiniBarChart data={daily_activity || []} dataKey="stages_completed"
                        label="Stages Completed / Day" color="bg-indigo-500" />
                </div>
            </div>

            {/* Products Made Table */}
            {products_made && products_made.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden print:break-inside-avoid">
                    <div className="bg-purple-50 px-5 py-3 border-b border-purple-100">
                        <h3 className="font-bold text-purple-800 text-sm flex items-center gap-2">
                            <Gem size={16} /> Products Worked On
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="text-left px-5 py-2 text-xs font-bold text-slate-500 uppercase">Product / Design</th>
                                    <th className="text-left px-5 py-2 text-xs font-bold text-slate-500 uppercase">Job Card</th>
                                    <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Stages</th>
                                    <th className="text-center px-5 py-2 text-xs font-bold text-slate-500 uppercase">Pieces</th>
                                    <th className="text-left px-5 py-2 text-xs font-bold text-slate-500 uppercase">Stage Types</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {products_made.map((p, i) => (
                                    <tr key={i} className="hover:bg-slate-50/50">
                                        <td className="px-5 py-2.5 font-bold text-slate-800">{p.design_name}</td>
                                        <td className="px-5 py-2.5 font-mono text-xs text-slate-500">{p.job_card_no}</td>
                                        <td className="px-5 py-2.5 text-center font-bold text-indigo-600">{p.count}</td>
                                        <td className="px-5 py-2.5 text-center font-bold text-emerald-600">{p.pieces}</td>
                                        <td className="px-5 py-2.5 text-xs text-slate-500">{(p.stages || []).join(', ')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Stage Detail Table */}
            {stage_detail && stage_detail.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden print:break-inside-avoid">
                    <div className="bg-indigo-50 px-5 py-3 border-b border-indigo-100 flex items-center justify-between">
                        <h3 className="font-bold text-indigo-800 text-sm flex items-center gap-2">
                            <Layers size={16} /> Stage Completion Detail
                        </h3>
                        <span className="text-[10px] font-bold text-indigo-400 uppercase">{stage_detail.length} records (max 50)</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase">Stage</th>
                                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase">Job Card</th>
                                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase">Design</th>
                                    <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Pieces</th>
                                    <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Delay</th>
                                    <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase">Completed</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {stage_detail.map((s, i) => (
                                    <tr key={i} className="hover:bg-slate-50/50">
                                        <td className="px-4 py-2 font-bold text-slate-700 text-xs">{s.stage_name}</td>
                                        <td className="px-4 py-2 font-mono text-xs text-slate-500">{s.job_card_no}</td>
                                        <td className="px-4 py-2 text-xs text-slate-600">{s.design_name}</td>
                                        <td className="px-4 py-2 text-center font-bold text-emerald-600">{s.pieces}</td>
                                        <td className="px-4 py-2 text-center">
                                            <span className={`font-bold text-xs ${s.delay_mins > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {s.delay_mins > 0 ? `+${s.delay_mins}m` : '—'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-xs text-slate-400 font-mono">
                                            {s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};


// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════
export default function WorkerReport() {
    const navigate = useNavigate();
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [period, setPeriod] = useState('month');

    return (
        <div className="flex h-screen bg-slate-50 font-sans text-slate-900 overflow-hidden">
            <style>{`
                @media (max-width: ${BREAKPOINTS.mobile}) {
                    .worker-report-content { padding: 16px !important; }
                }
                @media (min-width: ${BREAKPOINTS.mobile}) and (max-width: ${BREAKPOINTS.desktop}) {
                    .worker-report-content { padding: 24px !important; }
                }
            `}</style>
            {/* Sidebar */}
            <aside className="w-64 bg-gradient-to-b from-indigo-900 via-blue-900 to-slate-900 text-white flex flex-col shadow-2xl z-20 print:hidden">
                <div className="p-6 border-b border-white/10 flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                        <Gem className="text-white" size={24} />
                    </div>
                    <span className="text-xl font-black text-white tracking-tighter uppercase italic">
                        ZILVERRA<span className="text-indigo-400">JEWELS</span>
                    </span>
                </div>
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                    <div className="text-xs font-bold text-indigo-300 uppercase px-3 mt-2 mb-2">Reports</div>
                    <div className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 text-white">
                        <FileText size={20} className="text-indigo-400" /> Worker Report
                    </div>

                    <div className="text-xs font-bold text-indigo-300 uppercase px-3 mt-6 mb-2">Navigation</div>
                    <button onClick={() => navigate('/production/worker-performance')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200 hover:bg-white/5 transition-all">
                        <Trophy size={20} /> Leaderboard
                    </button>
                    <button onClick={() => navigate('/production')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-indigo-200 hover:bg-white/5 transition-all">
                        <ArrowLeft size={20} /> Back to Production
                    </button>
                </nav>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50/50">
                {/* Top Bar */}
                <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shadow-sm z-10 print:hidden">
                    <div className="flex items-center gap-2 text-sm">
                        <span onClick={() => navigate('/production')} className="text-slate-500 hover:text-indigo-600 cursor-pointer transition-colors">Home</span>
                        <span className="text-slate-300">/</span>
                        <span className="font-medium text-slate-800">Worker Report</span>
                        {selectedWorker && (
                            <>
                                <span className="text-slate-300">/</span>
                                <span className="font-medium text-indigo-600">{selectedWorker.name}</span>
                            </>
                        )}
                    </div>
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-xs font-semibold border border-emerald-100 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" /> System Online
                    </span>
                </header>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="worker-report-content max-w-6xl mx-auto p-8 space-y-6">

                        {/* Page Title + Search */}
                        <div className="flex items-start justify-between gap-8 print:hidden">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                                    <FileText className="text-indigo-500" size={28} /> Worker Search Report
                                </h1>
                                <p className="text-sm text-slate-500 mt-1">
                                    Search any worker and view their complete activity, attendance & payroll report.
                                </p>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="print:hidden">
                            <WorkerSearchBar onSelect={w => { setSelectedWorker(w); }} />
                        </div>

                        {/* Report Content */}
                        {selectedWorker ? (
                            <ReportView
                                worker={selectedWorker}
                                period={period}
                                onChangePeriod={setPeriod}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-80 text-slate-400 print:hidden">
                                <Search size={48} className="text-slate-200 mb-4" />
                                <p className="font-medium text-lg">Search for a worker to generate their report</p>
                                <p className="text-sm mt-1">Type a name, worker code, or role in the search bar above</p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
